import * as React from 'react'
import { Box, Card, ToggleButton, ToggleButtonGroup } from '@mui/material'
import FormatColorTextIcon from '@mui/icons-material/FormatColorText'
import { useTranslate } from 'ra-core'
import { useTheme, RaThemeOptions } from 'ra-ui-materialui'
import { useTiptapEditor } from 'ra-input-rich-text'
import { Editor } from '@tiptap/react'

import { grey, red, orange, yellow, green, blue, purple } from '@mui/material/colors'
import { TextColorButtonProps } from './type'

type OutsideListenerProps = {
  className?: string
  onClick: () => void
  children: React.ReactNode
}

/**
 * Hook that listens clicks outside of the passed ref
 */
const useOutsideListener = (
  /* eslint-disable */
  ref: React.MutableRefObject<any>,
  /* eslint-enable */
  onClick: () => void,
): void => {
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClick()
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return (): void => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, onClick])
}

/**
 * Component that listens if you click outside of it
 */
const OutsideListener: React.FC<OutsideListenerProps> = ({ className, onClick, children }) => {
  const wrapperRef = React.useRef(null)
  useOutsideListener(wrapperRef, onClick)

  return (
    <div className={className} ref={wrapperRef}>
      {children}
    </div>
  )
}

type ColorChoiceDialogProps = {
  editor: Editor | null
  close: () => void
}

const ColorChoiceDialog: React.FC<ColorChoiceDialogProps> = ({ editor, close }) => {
  const theme = useTheme() as RaThemeOptions
  const colors = [grey, red, orange, yellow, green, blue, purple]
  const shades = [900, 700, 500, 300, 100]

  const selectColor = (color: string): void => {
    editor.chain().focus().setColor(color).run()
    close()
  }

  return (
    <Card
      sx={{
        position: 'absolute',
        top: 38,
        left: 0,
        p: 1,
        border: `1px solid ${theme?.palette?.background?.default}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        zIndex: 1,
      }}
    >
      {shades.map((shade, line) => (
        <Box key={`shade-${shade}`} sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {colors.map((color, row) => (
            <Box
              key={`color-${line * colors.length + row + 1}`}
              sx={{
                width: 16,
                height: 16,
                cursor: 'pointer',
                backgroundColor: color[shade],
              }}
              onClick={(): void => selectColor(color[shade])}
            ></Box>
          ))}
        </Box>
      ))}
    </Card>
  )
}

export const FontColorButton: React.FC<TextColorButtonProps> = (props) => {
  const translate = useTranslate()
  const editor = useTiptapEditor()
  const [showColorChoiceDialog, setShowColorChoiceDialog] = React.useState<boolean>(false)

  const colorLabel = translate('ra.tiptap.color', { _: 'Color' })

  const displayColorChoiceDialog = (): void => {
    setShowColorChoiceDialog(true)
  }

  return editor ? (
    <Box sx={{ position: 'relative' }}>
      <OutsideListener onClick={(): void => setShowColorChoiceDialog(false)}>
        <ToggleButtonGroup>
          <ToggleButton
            aria-label={colorLabel}
            title={colorLabel}
            {...props}
            disabled={!editor?.isEditable}
            value='color'
            onClick={(): void => displayColorChoiceDialog()}
          >
            <FormatColorTextIcon fontSize='inherit' />
          </ToggleButton>
        </ToggleButtonGroup>
        {showColorChoiceDialog && (
          <ColorChoiceDialog editor={editor} close={(): void => setShowColorChoiceDialog(false)} />
        )}
      </OutsideListener>
    </Box>
  ) : null
}
